import {
  Button,
  ButtonProps,
  Flex,
  IconButton,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import * as DateFns from "date-fns";
import React from "react";
import { RiArrowRightSLine } from "react-icons/ri";
import { Card } from "src/components/Layout/Card";
import { RouterLink } from "src/components/Links/RouterLink";
import { RelativeTime } from "src/components/Text/RelativeTime";
import * as FSR from "src/generated/hooks/form-service/requests";
import { useOrganization } from "src/hooks/useOrganization";
import { formatMoney } from "src/services/format";
import { isNonEmptyString, isNotNull } from "src/services/predicates";
import * as Url from "src/services/url";
import { PaymentInfo } from "./PaymentInfo";

type PendingPaymentCardProps = {
  payment: FSR.PaymentForMe;
  payHandler: (payment: FSR.PaymentForMe) => Promise<void>;
  buttonProps?: ButtonProps;
  refetch: () => Promise<void>;
};
export const PendingPaymentCard: React.FC<PendingPaymentCardProps> = ({
  payment,
  payHandler,
  buttonProps,
  refetch,
}) => {
  React.useEffect(() => {
    if (
      payment.checkout.state !== "in_progress_by_other_user" ||
      !isNotNull(payment.checkout.expiresAt)
    ) {
      return;
    }

    const timeoutMs = Math.max(
      0,
      DateFns.differenceInMilliseconds(payment.checkout.expiresAt, new Date())
    );

    const timeoutId = window.setTimeout(() => {
      void refetch();
    }, timeoutMs);

    return () => window.clearTimeout(timeoutId);
  }, [payment.checkout.expiresAt, payment.checkout.state, refetch]);
  return (
    <Card showShadow fontSize="sm" useFlex gap="2">
      <PaymentInfo payment={payment} />
      <Flex alignItems="center" gap="2">
        <Text fontSize="lg" fontWeight="semibold" flex="1 1 0px">
          {formatMoney(payment.amountInCents, payment.currency)}
        </Text>
        {payment.checkout.state === "available" ||
        payment.checkout.state === "in_progress_by_current_user" ? (
          <Button
            flex="1 1 0px"
            onClick={() => payHandler(payment)}
            {...buttonProps}
          >
            Pay
          </Button>
        ) : null}
        {payment.checkout.state === "in_progress_by_other_user" ? (
          <Tooltip
            label={
              <>
                This payment is currently in progress in another session.{" "}
                {isNonEmptyString(payment.checkout.expiresAt) ? (
                  <>
                    You can try again in{" "}
                    {<RelativeTime timestamp={payment.checkout.expiresAt} />}
                  </>
                ) : null}
              </>
            }
          >
            <Flex flex="1 1 0px" tabIndex={0}>
              <Button flexGrow="1" isDisabled>
                In progress
              </Button>
            </Flex>
          </Tooltip>
        ) : null}
      </Flex>
    </Card>
  );
};

type PaidPaymentCardProps = {
  payment: FSR.PaymentForMe;
};
export const PaidPaymentCard: React.FC<PaidPaymentCardProps> = ({
  payment,
}) => {
  const organization = useOrganization();
  return (
    <Card
      showShadow
      useFlex
      fontSize="sm"
      alignItems="center"
      flexDirection="row"
      gap="2"
    >
      <PaymentInfo payment={payment} paid />
      <Flex gap="2" alignItems="center">
        <Text fontSize="lg" fontWeight="semibold" flexGrow="1">
          {formatMoney(payment.amountInCents, payment.currency)}
        </Text>
        <IconButton
          as={RouterLink}
          variant="ghost"
          colorScheme="gray"
          aria-label="See receipt"
          icon={<RiArrowRightSLine />}
          to={organization
            .map((org) => Url.Parent.Payment.receipt(org, payment.id))
            .withDefault("#")}
        />
      </Flex>
    </Card>
  );
};
